import { App } from 'antd';
import { useCallback, useMemo, useRef, useState } from 'react';

import { Confirm, Divider } from '@desygner/ui-common-components';
import { useTranslation } from '@desygner/ui-common-translation';

import { ShareAccessProps } from './ShareAccess';
import { AccessSwitchMode, ShareAccessLevel } from './ShareAccess.types';
import { StyledContentWrapper, StyledWrapper } from './ShareAccessContent.styles';
import { CopyLink } from './components/CopyLink';
import { ExternalShare, ExternalShareProps } from './components/ExternalShare';
import { SocialCategory, socialConfig } from './config';
import { getAccessDataMap } from './config/share-access.config';
import { isFeatureEnabled, share } from './share-access.utils';

export type ShareAccessContentProps = Pick<ShareAccessProps, 'isTeamShared' | 'isOwner'> & {
  projectName: string;
  shareLink: string;
  selectedAccess: ShareAccessLevel;
  features?: Partial<Record<ShareAccessLevel, boolean>>;
  accessSwitchMode?: AccessSwitchMode;
  onSelectedAccessChange: (access: ShareAccessLevel) => Promise<void>;
  projectType?: 'design' | 'template';
};

export function ShareAccessContent({
  projectName,
  shareLink,
  selectedAccess,
  onSelectedAccessChange,
  features,
  accessSwitchMode,
  isOwner,
  isTeamShared,
  projectType = 'design',
}: ShareAccessContentProps) {
  const { t } = useTranslation();
  const [confirmVisible, setConfirmVisible] = useState(false);
  const { message } = App.useApp();

  const accessDataMap = useMemo(() => getAccessDataMap(projectType), [projectType]);

  const pendingConfirmSocial = useRef<{
    groupId: string;
    itemId: string;
  } | null>(null);

  const isCollaborativeTeamMember = useMemo(
    () => isTeamShared && !isOwner,
    [isTeamShared, isOwner]
  );

  const isCollaborativeDesignUser = useMemo(
    () => projectType === 'design' && isCollaborativeTeamMember,
    [projectType, isCollaborativeTeamMember]
  );

  const groups = useMemo<ExternalShareProps['groups']>(() => {
    const externalShares: Record<SocialCategory[keyof SocialCategory], boolean> =
      projectType === 'template' || isCollaborativeDesignUser
        ? {
            socialMedia: false,
            messaging: true,
            translating: false,
            communication: false,
          }
        : {
            socialMedia: true,
            messaging: true,
            translating: true,
            communication: true,
          };

    return Object.values(socialConfig).reduce<ExternalShareProps['groups']>((acc, item) => {
      if (item.id && externalShares[item.id]) {
        acc.push({
          id: item.id,
          titleKey: item.titleKey,
          shares: item.values.map<ExternalShareProps['groups'][number]['shares'][number]>(
            (share) => ({
              id: share.id,
              iconUrl: share.iconUrl,
              label: share.name,
            })
          ),
        });
      }

      return acc;
    }, []);
  }, [projectType]);

  const changeAccess = useCallback(
    async (access: ShareAccessLevel) => {
      try {
        await onSelectedAccessChange(access);
        message.success(
          t('shared.share_viewer_access.toast_msg.success_prompt', {
            type: t(accessDataMap[access].typeKey),
          })
        );
      } catch (error) {
        message.error(
          t('shared.share_viewer_access.toast_msg.error_prompt', {
            type: t(accessDataMap[access].typeKey),
          })
        );
      }
    },
    [onSelectedAccessChange, message, t, accessDataMap]
  );

  const copyLink = useCallback(() => {
    navigator.clipboard.writeText(shareLink);
    message.success(t('shared.share_viewer_access.copied_link_text_button'));
  }, [shareLink, message, t]);

  const handleSocialShare = useCallback<ExternalShareProps['onItemClick']>(
    (item) => {
      const isTemplate = projectType === 'template';

      if (!isTemplate && !isFeatureEnabled(ShareAccessLevel.Public, features)) {
        return;
      }

      if (!isTemplate && !isCollaborativeDesignUser && selectedAccess !== ShareAccessLevel.Public) {
        pendingConfirmSocial.current = {
          groupId: item.groupId,
          itemId: item.itemId,
        };

        setConfirmVisible(true);
        return;
      }

      share({
        link: shareLink,
        groupId: item.groupId,
        itemId: item.itemId,
        name: projectName,
      });
    },
    [features, projectName, projectType, selectedAccess, shareLink]
  );

  const handleConfirm = useCallback(async () => {
    try {
      await changeAccess(ShareAccessLevel.Public);

      if (pendingConfirmSocial.current) {
        share({
          name: projectName,
          link: shareLink,
          ...pendingConfirmSocial.current,
        });

        pendingConfirmSocial.current = null;
      }
    } catch (error) {
      //error was handled in changeAccess
    } finally {
      setConfirmVisible(false);
    }
  }, [changeAccess, projectName, shareLink]);

  const isAnyShareEnabled = useMemo(() => groups.length > 0, [groups.length]);

  return (
    <StyledWrapper>
      {confirmVisible && (
        <Confirm
          type="warning"
          labels={{
            title: t('shared.share_viewer_access.change_access_title', {
              type: t(accessDataMap[selectedAccess].typeKey),
            }),
            description: t(accessDataMap[ShareAccessLevel.Public].confirmDescriptionKey),
            confirmButton: t('shared.share_viewer_access.allow_access_button', {
              type: t(accessDataMap[ShareAccessLevel.Public].typeKey),
            }),
            cancelButton: t('shared.share_viewer_access.cancel_button'),
          }}
          onCancel={() => setConfirmVisible(false)}
          onDismiss={() => setConfirmVisible(false)}
          onConfirm={handleConfirm}
        />
      )}
      <ExternalShare groups={groups} onItemClick={handleSocialShare} />
      {isAnyShareEnabled ? <Divider /> : null}
      <StyledContentWrapper>
        <CopyLink
          selectedAccess={selectedAccess}
          onSelectedAccessChange={changeAccess}
          onButtonClick={copyLink}
          features={features}
          accessSwitchMode={accessSwitchMode}
          projectType={projectType}
        />
      </StyledContentWrapper>
    </StyledWrapper>
  );
}
